/**
 * Pure helpers for the product context page (plan 4 R11).
 */

/** @param {object|null|undefined} product */
export function productIsArchived(product) {
  return !!(product && product.is_active === false);
}

/** @param {object|null|undefined} product */
export function readinessLabel(product) {
  if (productIsArchived(product)) return { label: 'Archived', tone: 'muted' };
  const depth = Number(product && product.analysis_depth);
  if (Number.isFinite(depth) && depth >= 3) return { label: 'Ready for chat', tone: 'ok' };
  if (Number(product && product.integration_count) > 0) return { label: 'Partly ready', tone: 'warn' };
  return { label: 'Needs a connector', tone: 'warn' };
}

/** @param {object|null|undefined} product */
export function platformsList(product) {
  const raw = product && product.platforms;
  if (Array.isArray(raw)) return raw.map((item) => String(item)).filter(Boolean);
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((item) => String(item)).filter(Boolean);
    } catch { /* plain csv */ }
    return raw.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

/**
 * Body for PUT /products/:id - only fields the API already accepts.
 * @param {{
 *   name?: string,
 *   slug?: string,
 *   description?: string,
 *   category?: string,
 *   short_intro?: string,
 *   stage?: string,
 *   store_category?: string,
 * }} form
 * @param {object} product
 */
export function productUpdatePayload(form, product) {
  const name = String(form.name != null ? form.name : product.name || '').trim();
  const slug = String(form.slug != null ? form.slug : product.slug || '').trim();
  if (!name) return { error: 'Name is required' };
  if (!slug) return { error: 'Slug is required' };
  return {
    body: {
      name,
      slug,
      description: String(form.description != null ? form.description : product.description || '').trim(),
      category: String(form.category != null ? form.category : product.category || '').trim() || product.category || 'app',
      short_intro: String(form.short_intro != null ? form.short_intro : product.short_intro || '').trim(),
      stage: String(form.stage != null ? form.stage : product.stage || '').trim() || product.stage || undefined,
      store_category: String(form.store_category != null ? form.store_category : product.store_category || '').trim()
        || product.store_category
        || undefined,
    },
  };
}

/**
 * Overview stat cells for the context panel.
 * @param {{
 *   product: object,
 *   integrations?: Array<object>,
 *   documentCount?: number,
 * }} opts
 */
export function overviewStats(opts) {
  const product = opts.product || {};
  const integrations = opts.integrations || [];
  const docs = opts.documentCount != null
    ? Number(opts.documentCount)
    : Number(product.document_count || 0);
  const ready = readinessLabel(product);
  return [
    { label: 'Slug', value: product.slug || '-' },
    { label: 'Category', value: product.category || product.store_category || '-' },
    { label: 'Connectors', value: String(integrations.length || product.integration_count || 0) },
    { label: 'Knowledge', value: `${Number.isFinite(docs) ? docs : 0} docs` },
    { label: 'Chat readiness', value: ready.label },
    { label: 'Stage', value: product.stage || '-' },
  ];
}

/**
 * @param {Array<{
 *   provider_display?: string,
 *   provider_name?: string,
 *   label?: string,
 *   health_status?: string,
 *   type?: string,
 *   credential_type?: string,
 * }>} integrations
 */
export function connectorRows(integrations) {
  return (integrations || []).map((row) => ({
    name: row.provider_display || row.provider_name || row.label || 'Connector',
    label: row.label || '-',
    type: row.type || row.credential_type || '-',
    health: row.health_status || 'unknown',
  }));
}

/** @param {string|null|undefined} status */
export function healthLabel(status) {
  const key = String(status || '').toLowerCase();
  if (key === 'healthy' || key === 'ok' || key === 'live') return { label: 'Live', tone: 'ok' };
  if (key === 'revoked' || key === 'misconfigured' || key === 'error') {
    return { label: 'Needs attention', tone: 'warn' };
  }
  if (key === 'untested' || key === 'unknown' || !key) return { label: 'Not verified', tone: 'muted' };
  return { label: status, tone: 'muted' };
}
